import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowDown, Bone, Dumbbell, Activity, Brain, Footprints, Accessibility, Stethoscope, Sparkles, Star, Quote } from "lucide-react";
import { MaskLine, Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import Hero3D from "@/components/Hero3D";
import { api, inr } from "@/lib/api";

const CATEGORY_ICON = {
  bone: Bone,
  muscle: Dumbbell,
  joint: Activity,
  nerve: Brain,
  sports: Footprints,
  geriatric: Accessibility,
  postsurgery: Stethoscope,
  wellness: Sparkles,
};

const MANIFESTO = [
  { n: "01", title: "Clinically Dosed", body: "Every formula matches the exact dosages used in peer-reviewed physical-therapy trials. No pixie-dusting, no proprietary blends." },
  { n: "02", title: "Recovery First", body: "Built alongside sports physiotherapists to close the gap between injury and full return-to-play." },
  { n: "03", title: "Neural to Skeletal", body: "From peripheral nerve repair to cartilage regeneration — a full-system approach to human durability." },
];

const TESTIMONIALS = [
  {
    name: "Dr. Ananya Menon",
    role: "Consultant Physiotherapist · Mumbai",
    photo: "https://images.unsplash.com/photo-1651008376811-b90baee60c1f?crop=faces&fit=crop&w=400&h=400&q=85",
    initials: "AM",
    stars: 5,
    quote:
      "I recommend Mag Forte D3 and PhysiomoveFlex to my post-op knee patients. Recovery timelines are visibly shorter and my patients keep re-ordering — that tells you everything.",
    tint: "#2F80ED",
  },
  {
    name: "Rohit Sharma",
    role: "Marathon Runner · Bengaluru",
    photo: "https://images.unsplash.com/photo-1552058544-f2b08422138a?crop=faces&fit=crop&w=400&h=400&q=85",
    initials: "RS",
    stars: 5,
    quote:
      "PhysioFuel AM-PM+ became non-negotiable in my training block. The evening tab actually helps me sleep and wake without joint soreness — huge difference at 60+ km weeks.",
    tint: "#34D8A0",
  },
  {
    name: "Priya Kapoor",
    role: "Post-Surgery Recovery · Delhi",
    photo: "https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?crop=faces&fit=crop&w=400&h=400&q=85",
    initials: "PK",
    stars: 5,
    quote:
      "Six weeks after my ACL surgery my physio suggested Physio Essential. My swelling dropped, mobility came back faster than expected. My whole family now trusts this brand.",
    tint: "#0A2540",
  },
  {
    name: "Anil Verma",
    role: "Age 68 · Osteoarthritis",
    photo: "https://images.unsplash.com/photo-1559963110-71b394e7494d?crop=faces&fit=crop&w=400&h=400&q=85",
    initials: "AV",
    stars: 5,
    quote:
      "Morning stiffness in my knees used to keep me in bed until 9. With the Collagen sachets for two months, I now walk 4 km before breakfast. My grandchildren joke I'm 40 again.",
    tint: "#2F80ED",
  },
  {
    name: "Dr. Kavya Nair",
    role: "Sports Rehab Specialist · Chennai",
    photo: "https://images.unsplash.com/photo-1607990281513-2c110a25bd8c?crop=faces&fit=crop&w=400&h=400&q=85",
    initials: "KN",
    stars: 5,
    quote:
      "The Vitamin B12 spray works so much faster than tablets for my neuropathy patients. I've moved my entire clinic protocol to Physio Essential formulas — clean labels, honest doses.",
    tint: "#34D8A0",
  },
  {
    name: "Karan Doshi",
    role: "Powerlifter · Pune",
    photo: "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?crop=faces&fit=crop&w=400&h=400&q=85",
    initials: "KD",
    stars: 5,
    quote:
      "Myo-Release ended my nightly calf cramps after the very first week. Zero interruptions during heavy squat blocks now — this is what a real physio-designed formula feels like.",
    tint: "#0A2540",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 12)));
    api.get("/categories").then(({ data }) => setCats(data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="section-dark relative min-h-screen overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBwaHlzaWNhbCUyMHRoZXJhcHl8ZW58MHx8fHwxNzg1NjcwNzkyfDA&ixlib=rb-4.1.0&q=85"
            alt="Physiotherapy recovery treatment"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/60 to-[#0A2540]/80" />
        </motion.div>

        <div className="relative z-10 mx-auto grid min-h-screen max-w-[1400px] grid-cols-1 gap-8 px-5 pt-28 pb-16 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:items-end">
          {/* LEFT — headline + CTAs (unchanged copy, buttons, colors) */}
          <div className="flex flex-col justify-end">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-overline text-[#34D8A0]"
            >
              <span className="h-px w-10 bg-[#34D8A0]" />
              Beyond Nutrition · Clinical Recovery
            </motion.div>

            <h1 className="font-display text-[13vw] font-extrabold leading-[0.86] tracking-tighter sm:text-[10vw] lg:text-[8.5rem]">
              <MaskLine delay={0.15}>RECOVER</MaskLine>
              <MaskLine delay={0.3} className="text-[#34D8A0]">BEYOND.</MaskLine>
            </h1>

            <div className="mt-10 flex flex-col items-start gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="max-w-md space-y-3"
              >
                <p className="font-display text-xl font-bold text-white/95">Clinical Nutrition for Recovery.</p>
                <p className="text-base leading-relaxed text-white/70">
                  Nutritional support designed to complement physiotherapy, rehabilitation and active recovery — across joint, muscle, bone, nerve and sports systems.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.8 }}
                className="flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/find-my-formula"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#34D8A0] px-8 py-4 font-semibold text-black transition-transform hover:scale-[1.03]"
                  data-testid="hero-find-formula-btn"
                >
                  Find My Recovery Formula
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/for-physiotherapists"
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 font-semibold text-white transition-colors hover:bg-white hover:text-black"
                  data-testid="hero-physio-btn"
                >
                  For Physiotherapists
                </Link>
              </motion.div>
            </div>

            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="mt-14 flex items-center gap-2 font-mono text-xs uppercase tracking-overline text-white/40"
            >
              <ArrowDown className="h-4 w-4" /> Scroll to explore
            </motion.div>
          </div>

          {/* RIGHT — Premium 3D product showcase */}
          <div className="relative flex items-center justify-center lg:h-full">
            <Hero3D containerRef={heroRef} />
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-black/10 bg-[#2F80ED] py-4 text-white">
        <Marquee speed={45} gradient={false}>
          {["CLINICAL NUTRITION FOR RECOVERY", "PHYSIOTHERAPIST-LED", "GMP CERTIFIED FACILITY", "NUTRITIONAL SUPPORT", "COMPLEMENTS REHABILITATION", "ASSESS · REHABILITATE · RECOVER"].map(
            (t, i) => (
              <span key={i} className="mx-8 font-mono text-sm uppercase tracking-overline">
                {t} <span className="mx-8 text-[#34D8A0]">✦</span>
              </span>
            )
          )}
        </Marquee>
      </div>

      {/* WHAT ARE YOU RECOVERING FROM? */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-24" data-testid="recover-from-section">
        <Reveal>
          <p className="mb-3 font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Recover By Condition</p>
          <h2 className="max-w-2xl font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
            What are you recovering from?
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-black/60">
            Every recovery journey starts with the right question. Pick the area that matches your current physiotherapy or rehabilitation focus.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { title: "Joint Pain & Stiffness", desc: "Knee OA · Hip OA · Cartilage · Mobility", to: "/conditions/knee-osteoarthritis", tint: "#2F80ED" },
            { title: "Muscle Recovery", desc: "Cramps · Fatigue · Training recovery", to: "/conditions/muscle-cramps", tint: "#34D8A0" },
            { title: "Bone Health", desc: "Vitamin D · Fracture recovery · Aging", to: "/conditions/bone-health", tint: "#2F80ED" },
            { title: "Nerve Health", desc: "B12 support · Peripheral nerves", to: "/conditions/b12-support", tint: "#34D8A0" },
            { title: "Sports Recovery", desc: "ACL · Training · Return-to-activity", to: "/conditions/acl-rehabilitation", tint: "#2F80ED" },
            { title: "Post-Surgery Recovery", desc: "Knee/Hip replacement · Fracture · Rehab", to: "/conditions/joint-replacement-recovery", tint: "#34D8A0" },
          ].map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
            >
              <Link
                to={c.to}
                className="group relative flex h-full flex-col justify-between overflow-hidden border border-black/10 bg-white p-8 transition-all hover:border-[#2F80ED]"
                data-testid={`condition-card-${i}`}
              >
                <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-10 blur-2xl" style={{ backgroundColor: c.tint }} />
                <div>
                  <div className="h-1 w-10" style={{ backgroundColor: c.tint }} />
                  <h3 className="mt-4 font-display text-xl font-bold leading-tight tracking-tight text-[#0A2540]">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-black/55">{c.desc}</p>
                </div>
                <div className="mt-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline text-[#2F80ED] transition-transform group-hover:translate-x-1">
                  Explore Recovery <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-black/10 pt-8">
          <p className="text-sm text-black/50">Not sure where to start?</p>
          <Link
            to="/find-my-formula"
            className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-6 py-3 font-mono text-xs uppercase tracking-overline text-white transition-colors hover:bg-[#2F80ED]"
            data-testid="explore-quiz-cta"
          >
            Find My Recovery Formula <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8">
        <Reveal>
          <p className="mb-3 font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Targeted Systems</p>
          <h2 className="max-w-2xl font-display text-4xl font-bold tracking-tight sm:text-5xl">
            Eight targeted systems. One recovery.
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-px overflow-hidden border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-4">
          {cats.map((c, i) => {
            const Icon = CATEGORY_ICON[c.key] || Activity;
            return (
              <Reveal key={c.key} delay={i * 0.08}>
                <Link
                  to={`/shop?category=${c.key}`}
                  className="group flex h-full flex-col justify-between bg-[#F9F9F6] p-8 transition-colors hover:bg-black hover:text-white"
                  data-testid={`category-${c.key}`}
                >
                  <div className="flex items-center justify-between">
                    <Icon className="h-8 w-8 text-[#2F80ED] transition-colors group-hover:text-[#34D8A0]" />
                    <span className="font-mono text-xs text-black/40 group-hover:text-white/40">
                      {String(c.count).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="mt-16">
                    <h3 className="font-display text-2xl font-semibold tracking-tight">{c.label}</h3>
                    <p className="mt-1 text-sm text-black/50 group-hover:text-white/60">{c.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="section-dark px-5 py-28 sm:px-8">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <p className="mb-3 font-mono text-xs uppercase tracking-overline text-[#34D8A0]">The Manifesto</p>
            <h2 className="max-w-3xl font-display text-4xl font-bold leading-tight tracking-tight sm:text-6xl">
              We don't sell hype. We engineer recovery.
            </h2>
          </Reveal>

          <div className="mt-20 space-y-px">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div className="grid items-start gap-6 border-t border-white/15 py-10 lg:grid-cols-[120px_1fr_1.5fr]">
                  <span className="font-mono text-4xl font-bold text-[#34D8A0]">{m.n}</span>
                  <h3 className="font-display text-2xl font-semibold tracking-tight">{m.title}</h3>
                  <p className="max-w-xl text-base leading-relaxed text-white/60">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8">
        <div className="flex items-end justify-between">
          <Reveal>
            <p className="mb-3 font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Best Sellers</p>
            <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">Featured Formulas</h2>
          </Reveal>
          <Link to="/shop" className="link-underline hidden font-mono text-sm uppercase tracking-overline sm:block" data-testid="view-all-link">
            View All →
          </Link>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* TRUST / EDITORIAL SPLIT */}
      <section className="mx-auto grid max-w-[1400px] gap-px overflow-hidden border-y border-black/10 bg-black/10 lg:grid-cols-2">
        <div className="relative min-h-[420px] overflow-hidden bg-[#F3F4F6]">
          <img
            src="https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBwaHlzaWNhbCUyMHRoZXJhcHl8ZW58MHx8fHwxNzg1NjcwNzkyfDA&ixlib=rb-4.1.0&q=85"
            alt="Physical therapy"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex flex-col justify-center bg-[#F9F9F6] p-10 sm:p-16">
          <Reveal>
            <p className="mb-4 font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Backed by Science</p>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
              Formulated with sports physiotherapists.
            </h2>
            <p className="mt-6 max-w-lg leading-relaxed text-black/60">
              Our team of clinicians and rehabilitation specialists reviews every ingredient and dose.
              Third-party lab tested for purity, potency and heavy metals — because your recovery can't afford guesswork.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-8">
              {[
                { k: "50k+", v: "Recoveries supported" },
                { k: "100%", v: "Lab-tested batches" },
                { k: "4.8★", v: "Average rating" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl font-bold">{s.k}</div>
                  <div className="mt-1 font-mono text-[11px] uppercase tracking-overline text-black/45">{s.v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Patient & Practitioner Testimonials */}
      <section className="border-y border-black/10 bg-[#F9F9F6] px-5 py-24 sm:px-8 sm:py-32" data-testid="testimonials-section">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Real Recovery Stories</p>
              <h2 className="mt-4 font-display text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
                Trusted by <span className="italic text-[#2F80ED]">physiotherapists,</span><br />
                loved by patients.
              </h2>
            </div>
            <div className="flex items-center gap-8 font-mono text-xs uppercase tracking-overline text-black/50">
              <div>
                <div className="font-display text-4xl font-bold text-[#0A2540]">4.9</div>
                <div className="mt-1 flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3 w-3 fill-[#2F80ED] text-[#2F80ED]" />
                  ))}
                </div>
                <div className="mt-1 text-[10px]">Avg rating</div>
              </div>
              <div>
                <div className="font-display text-4xl font-bold text-[#0A2540]">1,900+</div>
                <div className="mt-2 text-[10px]">Verified reviews</div>
              </div>
              <div>
                <div className="font-display text-4xl font-bold text-[#0A2540]">120+</div>
                <div className="mt-2 text-[10px]">Physio clinics</div>
              </div>
            </div>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={t.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="relative flex flex-col justify-between border border-black/10 bg-white p-8"
                data-testid={`testimonial-${i}`}
              >
                <div>
                  <Quote className="h-7 w-7 opacity-20" style={{ color: t.tint }} />
                  <div className="mt-4 flex items-center gap-0.5">
                    {[...Array(t.stars)].map((_, s) => (
                      <Star key={s} className="h-3.5 w-3.5 fill-[#2F80ED] text-[#2F80ED]" />
                    ))}
                  </div>
                  <blockquote className="mt-4 font-display text-lg leading-snug text-[#0A2540]">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                </div>
                <figcaption className="mt-8 flex items-center gap-4 border-t border-black/5 pt-6">
                  <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-full ring-2 ring-white" style={{ boxShadow: `0 0 0 2px ${t.tint}` }}>
                    <img
                      src={t.photo}
                      alt={t.name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    <div
                      className="absolute inset-0 -z-10 flex items-center justify-center font-mono text-sm font-bold text-white"
                      style={{ backgroundColor: t.tint }}
                    >
                      {t.initials}
                    </div>
                  </div>
                  <div>
                    <div className="font-display text-sm font-bold text-[#0A2540]">{t.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-overline text-black/50">{t.role}</div>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>

          {/* Trusted-by strip */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 border-t border-black/10 pt-10 font-mono text-[11px] uppercase tracking-overline text-black/40">
            <span>As trusted by</span>
            <span className="font-display text-lg font-bold text-black/60">Apollo Physio</span>
            <span>·</span>
            <span className="font-display text-lg font-bold text-black/60">Fortis Rehab</span>
            <span>·</span>
            <span className="font-display text-lg font-bold text-black/60">Kokilaben Sports</span>
            <span>·</span>
            <span className="font-display text-lg font-bold text-black/60">Delhi Marathon Med</span>
            <span>·</span>
            <span className="font-display text-lg font-bold text-black/60">Mumbai PT Council</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark px-5 py-28 text-center sm:px-8">
        <Reveal className="mx-auto max-w-3xl">
          <h2 className="font-display text-4xl font-bold leading-tight tracking-tighter sm:text-6xl">
            Your comeback starts <span className="text-[#34D8A0]">now.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl leading-relaxed text-white/60">
            Free shipping on orders over {inr(999)}. Secure checkout with Razorpay, UPI, GPay & Paytm.
          </p>
          <Link
            to="/shop"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#34D8A0] px-10 py-4 font-semibold text-black transition-transform hover:scale-[1.03]"
            data-testid="cta-shop-btn"
          >
            Start Shopping <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
